import { baseURL } from "../ApiConstant";
import { CommonApiResponse } from "../../Controllers/Types/ApiResponse";
import {
  Reminder,
  ReminderPayload,
  ReminderStatus,
} from "../../Controllers/Types/Reminder/ReminderInterface";

const getAuthHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const getResponseMessage = (data: any, fallback: string) => {
  return data?.message ?? data?.massage ?? fallback;
};

const parseResponse = async (
  response: Response,
  successMessage: string
): Promise<CommonApiResponse> => {
  let data: any = null;

  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (response.ok) {
    return {
      status: true,
      statusCode: response.status,
      data: data?.data ?? data,
      massage: getResponseMessage(data, successMessage),
    };
  }

  return {
    status: false,
    statusCode: response.status,
    errors: data?.errors,
    massage: getResponseMessage(data, "Unable to process reminder request."),
  };
};

export const getReminderListApi = async (): Promise<CommonApiResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/remainder-list`, {
      method: "GET",
      headers: getAuthHeaders(),
    });
    const result = await parseResponse(response, "Reminders loaded successfully.");
    const responseData = result.data;

    result.data = Array.isArray(responseData)
      ? responseData
      : Array.isArray(responseData?.data)
        ? responseData.data
        : [];

    return result;
  } catch {
    return {
      status: false,
      statusCode: 500,
      data: [],
      massage: "Unable to connect. Please try again later.",
    };
  }
};

export const createReminderApi = async (
  payload: ReminderPayload
): Promise<CommonApiResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/remainder-create`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return parseResponse(response, "Reminder created successfully.");
  } catch {
    return { status: false, statusCode: 500, massage: "Unable to connect." };
  }
};

export const updateReminderApi = async (
  id: number,
  payload: ReminderPayload
): Promise<CommonApiResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/remainder-update/${id}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return parseResponse(response, "Reminder updated successfully.");
  } catch {
    return { status: false, statusCode: 500, massage: "Unable to connect." };
  }
};

export const changeReminderStatusApi = async (
  id: number,
  status: ReminderStatus
): Promise<CommonApiResponse> => {
  try {
    const response = await fetch(
      `${baseURL}/api/remainder-status-changed/${id}`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ status }),
      }
    );

    return parseResponse(response, "Reminder status changed successfully.");
  } catch {
    return { status: false, statusCode: 500, massage: "Unable to connect." };
  }
};

export const deleteReminderApi = async (
  id: number
): Promise<CommonApiResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/remainder-delete/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    return parseResponse(response, "Reminder deleted successfully.");
  } catch {
    return { status: false, statusCode: 500, massage: "Unable to connect." };
  }
};

export type { Reminder };
